import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import connectOptions, { mergeProps } from '../../../utils/connectOptions';
import { Project } from '../../../components/Company/CompanyBuyer/AddOnProject/index';
import config from '../../../config';
import {
  handleAbortRequest,
  loadPage,
  savePage,
  clearError,
  revertChanges,
} from '../../../actions/company/buyer/addOnProject/main';
import { sortTarget } from '../../../actions/company/buyer/addOnProject/target';
import { clearTemplatePopupError } from '../../../actions/company/buyer/addOnProject/templatePopup';
import {
  saveApproval,
  updateApprovals,
  insertApprovalRow,
  switchApprovalRowToEdit,
  sortApproval,
  deleteApproval,
  loadApprovals,
} from '../../../actions/company/buyer/addOnProject/approval';
import { setTagsValidationError } from '../../../actions/company/buyer/addOnProject/projectTags';
import detectDirty from '../../../components/Project/detectDirty';
import { openInternalLink } from '../../../utils/url';
import { showError } from '../../../utils/MessagePopup';
import deleteApprovalConfirmation from '../../services/deleteApprovalConfirmation';
import { unwrap } from '../../../utils/ChangeSpy';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES } from '../../../utils/industryTagsHelper';
import { usePopupContext } from '../../../contexts/PopupContext';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

const types = config.values.getIn(['project', 'types']);
const IS_DEFAULT_ADDON = true;

/**
 * State container component for project page.
 *
 * @param props {Object}.
 * @param props.user {Immutable.Map} Current logged user.
 * @param props.projectData {Immutable.Map} Project information.
 * @param props.tags {Immutable.Map} Industry tags data.
 * @param props.users {Immutable.List} User list.
 * @param props.approvals {Immutable.List} Approval list.
 * @param props.templates {Immutable.List} Template list.
 * @param props.targets {Immutable.List} Target list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.savePending {Boolean} Saving status.
 */
const AddOnProjectContainer = props => {
  const {
    user,
    projectId,
    projectData,
    users,
    tags,
    approvals,
    common,
    templatePopup,
    isDuplicateCompany,
    handleAbortRequest,
    loadPage,
    savePage,
    clearError,
    revertChanges,
    sortTarget,
    clearTemplatePopupError,
    saveApproval,
    updateApprovals,
    insertApprovalRow,
    switchApprovalRowToEdit,
    sortApproval,
    deleteApproval,
    loadApprovals,
    setTagsValidationError,
    ...rest
  } = props;

  const { openPopup, closePopup, hasPopup, onClosePopup } = usePopupContext();
  const [savedDetails, setSavedDetails] = useState(null);
  const {
    addOnSaveCallback,
    removeOnSaveCallback,
    addValidateCallBacks,
    deleteValidateCallbacks,
  } = useEntityEventContext();

  const tagsRef = useRef(tags);
  const projectIdRef = useRef(projectId);

  tagsRef.current = tags;
  projectIdRef.current = projectId;

  const canEditData = !isDuplicateCompany;

  const validate = useCallback(() => {
    let error = '';
    const businessModels = unwrap(tagsRef.current.get(BUSINESS_MODELS));
    const industryCategories = unwrap(tagsRef.current.get(INDUSTRY_CATEGORIES));

    if (!businessModels.size) {
      error = error.concat('Business Model is required\n');
    }

    if (!industryCategories.size) {
      error = error.concat('Industry Tags is required');
    }

    setTagsValidationError(error);

    return error.length === 0;
  }, [setTagsValidationError]);

  const handleSave = useCallback(
    (form, callback) => {
      setTimeout(() => {
        if (projectIdRef.current) {
          if (form === 'approvals') {
            saveApproval(callback);
          } else {
            savePage(false); // true need to load data for a default project approvals
          }
        }
      }, 1);
    },
    [saveApproval, savePage],
  );

  useEffect(() => {
    if (projectId) {
      loadPage(projectId, [projectId], IS_DEFAULT_ADDON);
    }

    addValidateCallBacks(validate);
    addOnSaveCallback(handleSave);

    return () => {
      deleteValidateCallbacks(validate);
      removeOnSaveCallback(handleSave);
      revertChanges();
      handleAbortRequest();
    };
  }, []);

  const showErrorsPopup = useCallback(() => {
    const errors = common.get('errors');

    if (errors.size > 0) {
      const callback = () => clearError();

      showError(openPopup, errors, callback);
    } else if (templatePopup) {
      const templateErrors = templatePopup.get('errors');

      if (templateErrors && templateErrors.size > 0) {
        const callback = () => clearTemplatePopupError();

        showError(openPopup, templateErrors, callback);
      }
    }
  }, [common, templatePopup, openPopup, clearError, clearTemplatePopupError]);

  const hasMountedRef = useRef(false);
  const previousProjectIdRef = useRef(projectId);
  const previousCommonRef = useRef(common);
  const previousTemplatePopupRef = useRef(templatePopup);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousProjectIdRef.current = projectId;
      previousCommonRef.current = common;
      previousTemplatePopupRef.current = templatePopup;

      return;
    }

    if (projectId && !previousProjectIdRef.current) {
      loadPage(projectId, [projectId], IS_DEFAULT_ADDON);
    }

    if (common !== previousCommonRef.current || templatePopup !== previousTemplatePopupRef.current) {
      if (hasPopup()) {
        onClosePopup(() => {
          showErrorsPopup();
        });
      } else {
        showErrorsPopup();
      }
    }

    previousProjectIdRef.current = projectId;
    previousCommonRef.current = common;
    previousTemplatePopupRef.current = templatePopup;
  }, [projectId, common, templatePopup, hasPopup, onClosePopup, showErrorsPopup, loadPage]);

  const handleRowClick = useCallback(
    ({ row, type }) => {
      if (type === types.get('APPROVALS')) {
        switchApprovalRowToEdit(row.get('index'));
      }
    },
    [switchApprovalRowToEdit],
  );

  const handleRowDoubleClick = useCallback(
    ({ row, type }) => {
      const id = row.get('id');
      const rowProjectId = row.get('projectId');

      if (type === types.get('APPROVALS')) {
        const approval = approvals.find(a => a.get('id') === id);

        if (approval || id === 'no_approval_lists') {
          openInternalLink(`/project/${rowProjectId}/approval/${id}`, false);
        }
      } else if (type === types.get('TARGETS')) {
        openInternalLink(`/company/${id}`);
      }
    },
    [approvals],
  );

  const handleRowInsert = useCallback(
    data => {
      insertApprovalRow({ ...data, projectId });
    },
    [projectId, insertApprovalRow],
  );

  const deleteApprovalRowAfterConfirm = useCallback(
    (event, approvalId) => {
      deleteApproval({
        projectId,
        approvalId,
        afterSuccess: () => {
          loadApprovals({
            projectId,
            field: common.get('approvalSortField'),
            direction: common.get('approvalSortDirection'),
            isDefaultAddon: true,
          });
        },
      });
    },
    [projectId, common, deleteApproval, loadApprovals],
  );

  const handleDeleteApprovalRow = useMemo(
    () =>
      deleteApprovalConfirmation({
        deleteApproval: deleteApprovalRowAfterConfirm,
        user,
        openPopup,
        closePopup,
      }),
    [deleteApprovalRowAfterConfirm, user, openPopup, closePopup],
  );

  const handleRowChange = useCallback(
    ({ type, rowIndex, field, value }) => {
      if (type === 'approvals') {
        updateApprovals(rowIndex, field, value);
      }
    },
    [updateApprovals],
  );

  const handleApprovalChange = useCallback(
    params => {
      updateApprovals(params.data.index, params.colDef.field, params.newValue);
    },
    [updateApprovals],
  );

  const handleSort = useCallback(
    (event, { type, direction, field }) => {
      if (type === 'targets') {
        sortTarget(field, direction);
      } else if (type === 'approvals') {
        sortApproval(field, direction);
      }
    },
    [sortTarget, sortApproval],
  );

  const handleEditEnd = useCallback(() => {
    setSavedDetails(null);
  }, []);

  const handleEdit = useCallback(() => {
    setSavedDetails({ projectData, users, tags });
    openPopup('AddOnProjectDetailsPopup', { onClose: handleEditEnd });
  }, [projectData, users, tags, openPopup, handleEditEnd]);

  if (!user) return null;
  if (!projectId) return null;

  return (
    <Project
      {...rest}
      approvals={approvals}
      canEditData={canEditData}
      common={common}
      onApprovalChange={handleApprovalChange}
      onDeleteApprovalRow={handleDeleteApprovalRow}
      onEdit={handleEdit}
      onRowChange={handleRowChange}
      onRowClick={handleRowClick}
      onRowDoubleClick={handleRowDoubleClick}
      onRowInsert={handleRowInsert}
      onSave={handleSave}
      onSort={handleSort}
      projectData={projectData}
      savedDetails={savedDetails}
      tags={tags}
      users={users}
    />
  );
};

AddOnProjectContainer.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  isDuplicateCompany: PropTypes.bool.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  savePending: PropTypes.bool.isRequired,
  tags: PropTypes.instanceOf(Map).isRequired,
  targets: PropTypes.instanceOf(List).isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = state => {
  const { templatePopup, ...rest } = state.targetCompany.buyer.addOnProject;

  return {
    ...rest,
    user: state.auth.get('user'),
    projectId: Number(state.targetCompany.buyer.addOnProject.projectId),
    savePending: detectDirty(state.targetCompany.buyer.addOnProject),
    templatePopup,
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
  };
};

export { AddOnProjectContainer };
export default connect(
  mapStateToProps,
  {
    handleAbortRequest,
    loadPage,
    savePage,
    sortTarget,
    sortApproval,
    clearError,
    updateApprovals,
    insertApprovalRow,
    switchApprovalRowToEdit,
    clearTemplatePopupError,
    saveApproval,
    deleteApproval,
    loadApprovals,
    setTagsValidationError,
    revertChanges,
  },
  mergeProps,
  connectOptions,
)(memo(AddOnProjectContainer));
